import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";

import { AccountUser, useCreateAccountUser, useUpdateAccountUser } from "@/hooks/api/use-account-users";
import { Account } from "@/hooks/api/use-accounts";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { FilterOption } from "@/components/customTable/types";
import { FilterFetchSelect } from "@/components/fetch-select";
import { useAuth } from "@/components/core/auth-provider";

export interface AddUserProps {
	account: Account | null | undefined;
	user: AccountUser | null | undefined;
	isAddUserDrawerOpen: boolean;
	onAddUserDrawerClose: () => void;
}

const schema = zod.object({
	userEmail: zod.string().email("Must be a valid email").min(1, "Email is required"),
	role: zod.string().min(1, "Role is required"),
	roleTitle: zod.string(),
});

type Values = zod.infer<typeof schema>;

export function AddUser({
	account,
	user,
	isAddUserDrawerOpen,
	onAddUserDrawerClose,
}: AddUserProps): React.JSX.Element {
  const { user: currentUser } = useAuth();
	const isEdit = !!user;

	const createAccountUserMutation = useCreateAccountUser();
	const updateAccountUserMutation = useUpdateAccountUser();

	const defaultValues = {
		userEmail: user?.user.email || "",
		role: user?.role.name || "",
		roleTitle: user?.role.title || "",
	} satisfies Values;

	const {
		control,
		handleSubmit,
		watch,
		setError,
		setValue,
		clearErrors,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<Values>({
		defaultValues,
		resolver: zodResolver(schema),
	});

	const role = watch("role");
	const roleTitle = watch("roleTitle");

	React.useLayoutEffect(() => {
		reset({
			userEmail: user?.user.email || "",
			role: user?.role.name || "",
			roleTitle: user?.role.title || "",
		});
	}, [reset, user, isAddUserDrawerOpen]);

	const onSubmitForm = async (data: Values) => {
		if (!account) {
			return;
		}

		try {
			await (isEdit && user?.user?.id
				? updateAccountUserMutation.mutateAsync({
						accountId: account.id,
						userId: user.user.id,
						data: { role: data.role },
					})
				: createAccountUserMutation.mutateAsync({
						accountId: account.id,
						data: { role: data.role, userEmail: data.userEmail },
					}));

			onAddUserDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;

			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

  const showRoleSelect = user?.user?.email !== currentUser?.email;

	return (
		<CustomDrawer
			open={isAddUserDrawerOpen}
			onClose={onAddUserDrawerClose}
			title={isEdit ? "Edit User" : "Add User"}
			onSubmit={handleSubmit(onSubmitForm)}
			isSubmitButtonDisabled={
				isSubmitting || createAccountUserMutation.isPending || updateAccountUserMutation.isPending
			}
			submitButtonText={isEdit ? "Update User" : "Add User"}
			width="420px"
		>
			<form onSubmit={handleSubmit(onSubmitForm)}>
				<Grid container spacing={2}>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="userEmail"
							render={({ field }) => (
								<FormControl error={Boolean(errors.userEmail)} fullWidth disabled={isEdit}>
									<InputLabel>Email address</InputLabel>
									<OutlinedInput {...field} type="email" placeholder="Enter email address" />
									{errors.userEmail ? <CustomFormHelperError text={errors.userEmail.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>

          {showRoleSelect && (
            <Grid
              size={{
                md: 12,
                xs: 12,
              }}
            >
              <Controller
                control={control}
                name="role"
                rules={{
                  required: "Role is required",
                }}
                render={() => (
                  <FormControl error={Boolean(errors.role)} fullWidth>
                    <FilterFetchSelect
                      label="Role"
                      showInputLabel
                      errorText={errors.role?.message}
                      value={{ label: roleTitle, value: role } as FilterOption}
                      placeholder="Select a role"
                      required
                      onChange={(selected: FilterOption | string) => {
                        const value = typeof selected === "string" ? selected : (selected.value as string) || "";
                        const label = typeof selected === "string" ? selected : (selected.label as string) || "";

                        setValue("role", value);
                        setValue("roleTitle", label);
                        if (value === "") {
                          setError("role", { message: "Role is required" });
                        } else {
                          clearErrors("role");
                        }
                      }}
                      getOptionsUrl="/rbac/roles"
                      fetchLabel="title"
                      fetchValue="name"
                    />
                    {errors.role ? <CustomFormHelperError text={errors.role.message || ""} /> : null}
                  </FormControl>
                )}
              />
            </Grid>
          )}
				</Grid>
			</form>
		</CustomDrawer>
	);
}
